import React from "react";
import { ReportHeader } from "../dto/ReportDTO";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { makeStyles } from "@mui/styles";

interface Props<T> {
  /**
   * Table headers configuration
   */
  headers: ReportHeader[];
  /**
   * Data to be rendered
   */
  rows: T[];
  /**
   * Function used to render the content of the table (body).
   * Must map an object instance (element in prop `rows`) to a ReactElement
   */
  tableRow?: (instance: T) => React.ReactElement;

  /**
   * Triggers an event when clicking on a row if this function is provided.
   * Also, the cursor becomes a pointer on row hover
   */
  onRowClick?: (instance: T) => void;

  /**
   * If `true`, the size of the table will be "small"
   */
  denseTable?: boolean;
}

export default function ReportTable<T>({ headers, rows, tableRow, onRowClick, denseTable }: Props<T>) {
  const classes = useStyles();

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table" size={denseTable ? "small" : undefined}>
        <TableHead>
          <TableRow>
            {headers?.map((header, index) => (
              <TableCell
                key={index}
                align={header.align}
                width={header.width}
                style={{
                  fontWeight: "bold",
                }}
              >
                {header.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody className={onRowClick && classes.tableRow}>
          {tableRow &&
            rows.map((row, index) => (
              <TableRow
                key={index}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {tableRow!(row)}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const useStyles = makeStyles(() => ({
  tableRow: {
    cursor: "pointer",
    "& :hover": {
      background: "#e8eaed",
    },
  },
}));
